import { faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute } from '@tanstack/react-router';
import { AttachmentTemplateRules } from '@/components/shared/AttachmentTemplateRules';
import {
  PageHeader,
  PageHeaderFooter,
  PageHeaderFooterLeft,
  PageHeaderFooterRight,
} from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { FacilityProcessGroupOption } from '@/hooks/useGroupedFacilityProcessOptions';
import { useGroupedFacilityProcessOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { useTranslator } from '@/hooks/useTranslator';
import { ATTACHMENT_TEMPLATE_TRACING_TYPES } from '@/lib/constants/attachmentTemplate';
import { COUNTRY_OPTIONS } from '@/lib/constants/countryOptions';
import { queryClient } from '@/lib/utils/queryClient';
import {
  adminAttachmentTemplateDetailQueryOptions,
  useAdminAttachmentTemplateDetail,
} from '../../-hooks/useAdminAttachmentTemplateDetail';
import { toReadOnlyRule } from '../../-utils/transformers';

export const Route = createFileRoute(
  '/management/attachment-templates/(pages)/$id/',
)({
  loader: ({ params }) =>
    queryClient
      .ensureQueryData(adminAttachmentTemplateDetailQueryOptions(params.id))
      .catch(() => undefined),
  component: AttachmentTemplateDetailPage,
});

/**
 * Read-only view of one template, reached from the list's name link.
 *
 * No archive control. `POST /:id/archive` answers 403
 * `cannot_archive_global_template` for everything this screen can reach, since
 * `POST /` hard-codes `isGlobal: true` — a button here would fail on every row.
 * There is no delete route either, and the old dashboard offered neither.
 */
function AttachmentTemplateDetailPage() {
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const { getTranslation: t } = useTranslator({
    page: 'managementAttachmentTemplates',
  });

  const { data, error, refetch } = useAdminAttachmentTemplateDetail(id);
  const { data: fpGroups = [] } = useGroupedFacilityProcessOptions();
  const countryOptions = COUNTRY_OPTIONS;

  if (error || !data) {
    return <QueryErrorState error={error} onRetry={() => void refetch()} />;
  }

  const fpLabelById = new Map<string, string>(
    (fpGroups as FacilityProcessGroupOption[]).flatMap(g =>
      g.options.map(o => [o.key, o.label]),
    ),
  );
  const countryNameByCode = new Map(countryOptions.map(c => [c.key, c.name]));
  const tracingTypeLabel =
    ATTACHMENT_TEMPLATE_TRACING_TYPES[data.tracingType] ?? data.tracingType;

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <PageHeader>
        <div className='flex flex-col gap-1 min-w-0'>
          <h1 className='text-sm font-semibold text-foreground leading-tight'>
            {data.name}
          </h1>
          {data.description && (
            <p className='text-xs text-muted-foreground'>{data.description}</p>
          )}
        </div>

        <PageHeaderFooter>
          <PageHeaderFooterLeft>
            <Badge variant='outline'>{tracingTypeLabel}</Badge>
          </PageHeaderFooterLeft>
          <PageHeaderFooterRight>
            <Button
              variant='outline'
              onClick={() =>
                void navigate({
                  to: '/management/attachment-templates/$id/edit',
                  params: { id },
                })
              }
            >
              <FontAwesomeIcon icon={faPen} />
              {t('detail.edit')}
            </Button>
          </PageHeaderFooterRight>
        </PageHeaderFooter>
      </PageHeader>

      <AttachmentTemplateRules
        rules={data.rules.map(toReadOnlyRule)}
        backupRule={data.backupRule ? toReadOnlyRule(data.backupRule) : null}
        labels={{
          ruleLabel: n => t('detail.ruleLabel', { n }),
          backupSeparator: t('detail.backupSeparator'),
          backupRuleTitle: t('detail.backupRuleTitle'),
          noRules: t('detail.noRules'),
        }}
        fpLabelById={fpLabelById}
        countryNameByCode={countryNameByCode}
      />
    </div>
  );
}
